import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isObject from '@cotyorg/ccx-utils/js/helpers/isObject';
import NavigationLink from '@cotyorg/ccx-molecules/NavigationLink';
import Button from '@cotyorg/ccx-atoms/Button';
import Icon from '@cotyorg/ccx-atoms/Icon';

const isSelected = (selectedSlug, slug, pages) => (
  selectedSlug === slug || (Array.isArray(pages) && pages.map(page => page.slug).includes(selectedSlug))
);

const generateLinkClassNames = ({
  navigation,
  slug,
  pages,
}) => classNames(
  'navigation__link',
  'navigation__link--hidden-sub-nav',
  'grid-x',
  'align-middle',
  {
    'navigation__link--selected': isSelected(navigation.selectedSlug, slug, pages),
  },
);

const generateNavigationClassNames = ({ extraClass }) => classNames(
  'navigation',
  'navigation--hidden-sub-nav',
  {
    [`${extraClass}`]: extraClass,
  },
);

const NavigationTopLevel = ({
  navigation,
  extraClass,
  NestedListComponent,
  isMobile,
}) => {
  const [openSubmenus, setOpenSubmenus] = useState({});

  const toggleSubmenu = (slug) => {
    setOpenSubmenus((prev) => {
    const isCurrentlyOpen = !!prev[slug];
    if (isCurrentlyOpen) {
      return {};
    }
    return { [slug]: true };
  });
  };

  const setSubmenuOpen = (slug, isOpen) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [slug]: isOpen,
    }));
  };

  if (!isObject(navigation) || !Array.isArray(navigation.pages)) {
    return null;
  }

  return (
    <nav className={generateNavigationClassNames({ extraClass })}>
      <ul className="navigation__list navigation__list--hidden-sub-nav" data-test="navigation-list">
        {
          navigation.pages.map((page) => {
            const {
              slug,
              name,
              pages,
              url,
              openInNewWindow,
            } = page;
            const hasSubmenu = pages && !!pages.length;

            return (
              <li
                key={slug}
                className="navigation__list-item navigation__list-item--hidden-sub-nav"
                data-test="navigation-list-item"
                onMouseEnter={() => hasSubmenu && setSubmenuOpen(slug, true)}
                onMouseLeave={() => hasSubmenu && setSubmenuOpen(slug, false)}
              >
                {hasSubmenu ? (
                  <Button
                    type="button"
                    onClick={() => toggleSubmenu(slug)}
                    aria-expanded={!!openSubmenus[slug]}
                    aria-controls={`submenu-${slug}`}
                    extraClass={generateLinkClassNames({ navigation, slug, pages })}
                  >
                    <span className="navigation__link-text navigation__link-text--hidden-sub-nav cell">
                      {name}
                      <Icon iconSrc="/ccx-files/assets/arrow-right.svg" />
                    </span>
                  </Button>
                ) : (
                  <NavigationLink
                    name={name}
                    pages={pages}
                    url={url}
                    openInNewWindow={openInNewWindow}
                    extraClass={generateLinkClassNames({ navigation, slug, pages })}
                    textExtraClass="navigation__link-text navigation__link-text--hidden-sub-nav cell"
                  />
                )}
                {hasSubmenu && openSubmenus[slug] && (
                  <NestedListComponent
                    page={page}
                    selectedSlug={navigation.selectedSlug}
                    isMobile={isMobile}
                    id={`submenu-${slug}`}
                  />
                )}
              </li>
            );
          })
        }
      </ul>
    </nav>
  );
};

NavigationTopLevel.propTypes = {
  navigation: PropTypes.shape({
    pages: PropTypes.arrayOf(PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      pages: PropTypes.array,
      openInNewWindow: PropTypes.bool,
    })),
  }),
  extraClass: PropTypes.string,
  NestedListComponent: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

export default NavigationTopLevel;
